import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Descriptions,
    Drawer,
    Form,
    Input,
    List,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Tabs,
    Tag,
    Timeline,
    Tooltip,
    Switch,
    Typography,
    Upload,
    message,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    BellOutlined,
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    LinkOutlined,
    DisconnectOutlined,
    MailOutlined,
    PlusOutlined,
    PaperClipOutlined,
    SendOutlined,
    UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import {
    useGetCrmCompanyQuery,
    useUpdateCrmCompanyMutation,
    useLinkCrmCompanyMutation,
    useUnlinkCrmCompanyMutation,
    useGetCrmActivitiesQuery,
    useCreateCrmActivityMutation,
    useCreateCrmContactMutation,
    useDeleteCrmContactMutation,
    useGetCrmTasksQuery,
    useCreateCrmTaskMutation,
    useUpdateCrmTaskMutation,
    useGetAdminOrganizationsQuery,
    useGetCrmEmailTemplatesQuery,
    useGetCrmEmailSendersQuery,
    useSendCrmEmailMutation,
    useLogCrmReceivedEmailMutation,
    useLazyGetCrmInboundAttachmentUrlQuery,
    useLazyGetCrmSentAttachmentUrlQuery,
    useUploadMailAttachmentMutation,
    useGetAdminOrganizationUsersQuery,
    useGetOrganizationActivityQuery,
} from '../../../services/nestjs-base-api';
import {
    ICrmActivity,
    ICrmPipelineStage,
    IMailStagedAttachment,
} from '../../../services/nestjs-base-api/types';
import { useAuth } from '../../../hooks/useAuth';
import { HealthDot, LifecycleTag, SourceIcon, formatAest, isPast, utcFromAestWall } from './crmUi';
import { CrmRichTextEditor, EmailBodyHtml, isQuillEmpty } from './CrmRichTextEditor';

const { Text } = Typography;
const { TextArea } = Input;

const LOST_REASONS = [
    'no_response',
    'no_budget',
    'bad_fit',
    'timing',
    'chose_competitor',
    'price',
    'other',
];

const activityDot: Record<string, string> = {
    note: 'blue',
    call: 'purple',
    meeting: 'purple',
    stage_change: 'orange',
    sheet_sync: 'cyan',
    system: 'gray',
    task: 'green',
    signal: 'red',
    platform: 'gray',
    email_out: 'cyan',
    email_in: 'blue',
    email_event: 'green',
};

interface CrmCompanyDrawerProps {
    companyId: number | null;
    stages: ICrmPipelineStage[];
    onClose: () => void;
}

export const CrmCompanyDrawer: React.FC<CrmCompanyDrawerProps> = ({
    companyId,
    stages,
    onClose,
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';

    const { data: company, isLoading } = useGetCrmCompanyQuery(
        { jwtToken, id: companyId! },
        { skip: !companyId },
    );
    const { data: activities } = useGetCrmActivitiesQuery(
        { jwtToken, companyId: companyId!, limit: 100 },
        { skip: !companyId },
    );
    const { data: tasks } = useGetCrmTasksQuery(
        { jwtToken, companyId: companyId! },
        { skip: !companyId },
    );

    const [updateCompany] = useUpdateCrmCompanyMutation();
    const [linkCompany] = useLinkCrmCompanyMutation();
    const [unlinkCompany] = useUnlinkCrmCompanyMutation();
    const [createActivity, createActivityResult] = useCreateCrmActivityMutation();
    const [createContact] = useCreateCrmContactMutation();
    const [deleteContact] = useDeleteCrmContactMutation();
    const [createTask] = useCreateCrmTaskMutation();
    const [updateTask] = useUpdateCrmTaskMutation();

    const [noteBody, setNoteBody] = useState('');
    const [notesDraft, setNotesDraft] = useState<string | null>(null);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [orgSearch, setOrgSearch] = useState('');
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    // Controls the template picker so it can be reset with the rest of the form
    // (a bare Select keeps its selection after the modal closes, leaving a
    // template "chosen" over an empty body on re-open).
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);
    // Off by default: the timeline exists for notes and emails, and product
    // activity is an extra lens rather than the main event.
    const [showProductActivity, setShowProductActivity] = useState(false);
    // Which email bodies are expanded inline in the timeline.
    const [expandedEmails, setExpandedEmails] = useState<Set<number>>(new Set());
    const [receivedModalOpen, setReceivedModalOpen] = useState(false);
    const [receivedForm] = Form.useForm();
    // Set = review step showing the exact email before commit-to-send.
    const [emailPreview, setEmailPreview] = useState<{
        crmContactId?: number;
        to: string[];
        cc: string[];
        subject: string;
        body: string;
        sendAsUserId?: number;
        fromLabel: string;
        fromAddress: string;
        branded?: boolean;
        allowUnfilledPlaceholders?: boolean;
        attachments: IMailStagedAttachment[];
    } | null>(null);
    // Files staged for the email being composed. They go through the same
    // upload Platform Mail uses, so the server-side limits are shared.
    const [emailAttachments, setEmailAttachments] = useState<IMailStagedAttachment[]>([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [uploadAttachment] = useUploadMailAttachmentMutation();
    const [getSentAttachmentUrl] = useLazyGetCrmSentAttachmentUrlQuery();
    const [contactForm] = Form.useForm();
    const [taskForm] = Form.useForm();
    const [emailForm] = Form.useForm();

    const { data: emailTemplates } = useGetCrmEmailTemplatesQuery(
        { jwtToken },
        { skip: !emailModalOpen },
    );
    const [sendCrmEmail, sendCrmEmailResult] = useSendCrmEmailMutation();
    const [logReceivedEmail, logReceivedEmailResult] = useLogCrmReceivedEmailMutation();
    const [getInboundAttachmentUrl] = useLazyGetCrmInboundAttachmentUrlQuery();

    // Kept in step with MailAttachmentsService.MAX_ATTACHMENT_BYTES: rejecting
    // here saves a round trip, the server still enforces it.
    const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
    const handleAttachmentUpload = async (file: File): Promise<boolean> => {
        if (file.size > MAX_ATTACHMENT_BYTES) {
            message.error(intl.formatMessage({ id: 'mail.attachmentTooLarge' }));
            return false;
        }
        setUploadingAttachment(true);
        try {
            const staged = await uploadAttachment({ jwtToken, file }).unwrap();
            setEmailAttachments((current) => [...current, staged]);
        } catch (error: any) {
            message.error(error?.data?.message || intl.formatMessage({ id: 'mail.attachmentFailed' }));
        } finally {
            setUploadingAttachment(false);
        }
        return false;
    };

    // Any active platform admin can be the name on the email — outreach often
    // lands better from a colleague than from whoever happens to be logged in.
    const { data: emailSenders } = useGetCrmEmailSendersQuery(
        { jwtToken },
        { skip: !emailModalOpen },
    );
    const sendAsUserId = Form.useWatch('sendAsUserId', emailForm) as number | undefined;
    const selectedSender =
        (emailSenders || []).find((s) => s.userId === (sendAsUserId ?? user?.id)) ?? null;
    const senderFirstName = selectedSender?.firstName || user?.firstName || 'You';
    const senderFromLabel = selectedSender?.fromLabel || `${senderFirstName} at BrandScience.ai`;
    // Which address it actually leaves as — the sender's own when the API says
    // they're cleared for one, otherwise the shared inbox. Resolved server-side;
    // this is display only.
    const senderAddress = selectedSender?.address || '';

    // Everyone selectable as a recipient: the account's CRM contacts plus the
    // linked organisation's platform users (any status — a suspended user's
    // address is still theirs), deduped by email.
    const { data: orgUsersData } = useGetAdminOrganizationUsersQuery(
        { jwtToken, organizationId: company?.organisationId ?? 0 },
        { skip: !emailModalOpen || !company?.organisationId },
    );
    const orgUsers = orgUsersData?.data || [];
    const peopleOptions: { value: string; label: string }[] = [];
    {
        const seen = new Set<string>();
        (company?.contacts || [])
            .filter((c) => !!c.email)
            .forEach((c) => {
                const email = c.email!.toLowerCase();
                if (seen.has(email)) return;
                seen.add(email);
                peopleOptions.push({ value: email, label: `${c.name || email} <${email}>` });
            });
        orgUsers
            .filter((u) => !!u.email)
            .forEach((u) => {
                const email = u.email.toLowerCase();
                if (seen.has(email)) return;
                seen.add(email);
                const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
                peopleOptions.push({
                    value: email,
                    label: `${name || email} <${email}>${String(u.status) !== 'ACTIVE' ? ' · not activated' : ''}`,
                });
            });
    }

    // Client-side substitution so the sender sees exactly what will be sent.
    // {senderName} follows the chosen sender, not the logged-in user.
    // {contactName}/{firstName} resolve from the FIRST To recipient — a CRM
    // contact if the email matches one, else the matching platform user.
    const substituteTemplate = (text: string, recipientEmail?: string): string => {
        const email = recipientEmail?.toLowerCase();
        const contactByEmail = email
            ? (company?.contacts || []).find((c) => c.email?.toLowerCase() === email)
            : undefined;
        const userByEmail = email
            ? orgUsers.find((u) => u.email?.toLowerCase() === email)
            : undefined;
        const fallbackContact =
            (company?.contacts || []).find((c) => c.isPrimary) ?? company?.contacts?.[0];
        const name =
            contactByEmail?.name ||
            (userByEmail
                ? [userByEmail.firstName, userByEmail.lastName].filter(Boolean).join(' ')
                : '') ||
            fallbackContact?.name ||
            '';
        return text
            .replace(/\{contactName\}/g, name)
            .replace(/\{firstName\}/g, name.split(' ')[0] || '')
            .replace(/\{companyName\}/g, company?.name || '')
            .replace(/\{senderName\}/g, senderFirstName === 'You' ? '' : senderFirstName);
    };

    const { data: orgSearchResults, isFetching: orgSearchLoading } =
        useGetAdminOrganizationsQuery(
            { jwtToken, search: orgSearch, limit: 20 },
            { skip: !linkModalOpen },
        );

    useEffect(() => {
        setNoteBody('');
        setNotesDraft(null);
    }, [companyId]);

    // Product activity for the linked organisation — the same merged PostHog +
    // platform-event stream as the org page's User Journey tab. Only available
    // once the CRM account is linked to an organisation; unlinked prospects
    // have no users to match on.
    const { data: productActivity } = useGetOrganizationActivityQuery(
        { jwtToken, organisationId: company?.organisationId as number, days: 90 },
        { skip: !company?.organisationId || !showProductActivity },
    );

    /**
     * Raw clickstream would bury the notes and emails this timeline exists for,
     * so collapse consecutive in-app events into one entry per session (split
     * on 30-minute idle gaps) while leaving platform events — logins, email
     * delivered/opened/clicked — as individual high-signal entries.
     */
    const productTimelineItems = useMemo(() => {
        const events = productActivity?.events || [];
        if (events.length === 0) return [];

        const items: { at: string; color: string; node: React.ReactNode }[] = [];
        let session: typeof events = [];

        const flushSession = () => {
            if (session.length === 0) return;
            const start = dayjs(session[0].timestamp);
            const end = dayjs(session[session.length - 1].timestamp);
            const paths = Array.from(
                new Set(session.map((e) => e.path).filter(Boolean)),
            ).slice(0, 4);
            const clicked = session
                .map((e) => e.elementText)
                .filter(Boolean)
                .slice(0, 3);
            items.push({
                at: session[0].timestamp,
                color: 'blue',
                node: (
                    <>
                        <Text>
                            <FormattedMessage
                                id="crm.drawer.inAppSession"
                                defaultMessage="In-app session · {count} events · {mins} min"
                                values={{
                                    count: session.length,
                                    mins: Math.max(1, end.diff(start, 'minute')),
                                }}
                            />
                        </Text>
                        {clicked.length > 0 && (
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {clicked.map((c) => `“${c}”`).join(', ')}
                                </Text>
                            </div>
                        )}
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {paths.join(' · ')}
                            </Text>
                        </div>
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {start.format('YYYY-MM-DD HH:mm')}
                                {session[0].userEmail ? ` · ${session[0].userEmail}` : ''}
                            </Text>
                        </div>
                    </>
                ),
            });
            session = [];
        };

        let previous: dayjs.Dayjs | null = null;
        events.forEach((e) => {
            const at = dayjs(e.timestamp);
            if (e.source === 'platform') {
                // CRM_* platform events are already in this timeline as CRM
                // activities (a send writes both an email_out activity and a
                // CRM_EMAIL_SENT event), so including them here would double
                // every entry. The org page keeps them — it has no CRM feed.
                if (e.event.startsWith('CRM_')) return;
                // A platform event ends the current in-app run so ordering stays
                // truthful — the session summary must not swallow it.
                flushSession();
                previous = null;
                items.push({
                    at: e.timestamp,
                    color: e.event.startsWith('EMAIL_') || e.event === 'CRM_EMAIL_SENT' ? 'green' : 'purple',
                    node: (
                        <>
                            <Text>{e.event.toLowerCase().replace(/_/g, ' ')}</Text>
                            {e.detail && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{e.detail}</Text>
                                </div>
                            )}
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {at.format('YYYY-MM-DD HH:mm')}
                                    {e.userEmail ? ` · ${e.userEmail}` : ''}
                                </Text>
                            </div>
                        </>
                    ),
                });
                return;
            }
            if (previous && at.diff(previous, 'minute') > 30) flushSession();
            session.push(e);
            previous = at;
        });
        flushSession();
        return items;
    }, [productActivity]);

    const toggleEmailExpanded = (id: number) => {
        setExpandedEmails((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleLogReceived = async (values: any) => {
        // Narrows companyId for the call below — the drawer's early return sits
        // after this handler is defined, so TS can't infer it here.
        if (!companyId) return;
        try {
            await logReceivedEmail({
                jwtToken,
                companyId,
                payload: {
                    subject: values.subject,
                    body: values.body,
                    fromEmail: values.fromEmail || undefined,
                    // The time it actually arrived, so the timeline stays truthful.
                    receivedAt: (values.receivedAt || dayjs()).toISOString(),
                },
            }).unwrap();
            message.success(intl.formatMessage({ id: 'crm.email.receivedLogged' }));
            receivedForm.resetFields();
            setReceivedModalOpen(false);
        } catch (e: any) {
            message.error(e?.data?.message || 'Failed to log received email');
        }
    };

    // SYR-92 Phase 4 — prefill the compose modal as a reply-all to an email
    // timeline entry. Counterparty = everyone on the original except our own
    // shared address; threading back to the account is automatic (the reply
    // will thread-match on our next inbox sync via its References header).
    const openReply = (activity: ICrmActivity) => {
        const meta = (activity.metadata || {}) as any;
        const isOurs = (addr: string) =>
            addr.toLowerCase().endsWith('@brandscience.ai');
        const toArr: string[] = Array.isArray(meta.to)
            ? meta.to
            : meta.to
              ? [meta.to]
              : [];
        const ccArr: string[] = Array.isArray(meta.cc) ? meta.cc : [];
        const counterparty = (
            activity.type === 'email_in' ? [meta.from, ...toArr] : toArr
        ).filter((a: unknown): a is string => typeof a === 'string' && !!a && !isOurs(a));
        const cc = ccArr.filter((a) => !!a && !isOurs(a));
        const rawSubject = (meta.subject || '').trim();
        emailForm.setFieldsValue({
            toPeople: [],
            to: Array.from(new Set(counterparty.map((a) => a.toLowerCase()))),
            cc: Array.from(new Set(cc.map((a) => a.toLowerCase()))),
            subject: rawSubject
                ? /^re:/i.test(rawSubject)
                    ? rawSubject
                    : `Re: ${rawSubject}`
                : '',
            sendAsUserId: user?.id,
        });
        setEmailModalOpen(true);
    };

    const activityToTimelineItem = (activity: ICrmActivity) => {
        const meta = (activity.metadata || {}) as any;
        const isEmail = activity.type === 'email_out' || activity.type === 'email_in';
        // Open/click counts are accumulated onto the parent email row by the
        // SES events webhook.
        const events = (meta.events || {}) as Record<string, number>;
        const expanded = expandedEmails.has(activity.id);

        return {
            key: activity.id,
            color: activityDot[activity.type] || 'gray',
            children: (
                <>
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{activity.body}</Text>
                    {isEmail && (
                        <div style={{ marginTop: 4 }}>
                            <Space size={4} wrap>
                                {activity.type === 'email_out' ? (
                                    <Tag color="cyan" icon={<SendOutlined />}>
                                        <FormattedMessage id="crm.email.directionSent" defaultMessage="Sent" />
                                    </Tag>
                                ) : (
                                    <Tag color="blue" icon={<MailOutlined />}>
                                        <FormattedMessage id="crm.email.directionReceived" defaultMessage="Received" />
                                    </Tag>
                                )}
                                {/* Sent from the Mail area rather than this
                                    drawer: link back to the conversation so the
                                    thread is one click away from the card. */}
                                {meta.source === 'platform_mail' && meta.threadKey && (
                                    <Link to={`/admin/mail?thread=${encodeURIComponent(meta.threadKey)}`}>
                                        <Tag color="geekblue" icon={<MailOutlined />}>
                                            <FormattedMessage id="crm.email.viewInMail" defaultMessage="View in Mail" />
                                        </Tag>
                                    </Link>
                                )}
                                {events.delivery > 0 && (
                                    <Tag color="green">
                                        <FormattedMessage id="crm.email.delivered" defaultMessage="Delivered" />
                                    </Tag>
                                )}
                                {events.open > 0 && (
                                    <Tag color="green">
                                        <FormattedMessage
                                            id="crm.email.openedCount"
                                            defaultMessage="Opened {count, plural, one {once} other {# times}}"
                                            values={{ count: events.open }}
                                        />
                                    </Tag>
                                )}
                                {events.click > 0 && (
                                    <Tag color="magenta">
                                        <FormattedMessage
                                            id="crm.email.clickedCount"
                                            defaultMessage="Clicked {count, plural, one {once} other {# times}}"
                                            values={{ count: events.click }}
                                        />
                                    </Tag>
                                )}
                                {meta.body && (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ padding: 0 }}
                                        onClick={() => toggleEmailExpanded(activity.id)}
                                    >
                                        {expanded ? (
                                            <FormattedMessage id="crm.email.hideContent" defaultMessage="Hide message" />
                                        ) : (
                                            <FormattedMessage id="crm.email.readContent" defaultMessage="Read message" />
                                        )}
                                    </Button>
                                )}
                                {(meta.from || meta.to) && (
                                    <Button
                                        type="link"
                                        size="small"
                                        style={{ padding: 0 }}
                                        icon={<SendOutlined />}
                                        onClick={() => openReply(activity)}
                                    >
                                        <FormattedMessage id="crm.email.reply" defaultMessage="Reply" />
                                    </Button>
                                )}
                            </Space>
                            {expanded && (meta.from || meta.to || (meta.cc && meta.cc.length > 0)) && (
                                <div style={{ marginTop: 6 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {activity.type === 'email_in' && meta.from && (
                                            <>
                                                <FormattedMessage id="crm.email.fromLabel" defaultMessage="From" />
                                                {': '}
                                                {meta.fromName ? `${meta.fromName} <${meta.from}>` : meta.from}
                                                {(meta.to?.length > 0 || meta.cc?.length > 0) && ' · '}
                                            </>
                                        )}
                                        {(Array.isArray(meta.to) ? meta.to.length > 0 : !!meta.to) && (
                                            <>
                                                <FormattedMessage id="crm.email.to" defaultMessage="To" />
                                                {': '}
                                                {/* Pre-SYR-92 activities store a single string. */}
                                                {Array.isArray(meta.to) ? meta.to.join(', ') : meta.to}
                                            </>
                                        )}
                                        {meta.cc?.length > 0 && (
                                            <>
                                                {' · '}
                                                <FormattedMessage id="crm.email.cc" defaultMessage="CC" />
                                                {': '}
                                                {meta.cc.join(', ')}
                                            </>
                                        )}
                                    </Text>
                                </div>
                            )}
                            {expanded && meta.body && (
                                <EmailBodyHtml
                                    body={meta.body}
                                    style={{
                                        marginTop: 8,
                                        padding: '10px 12px',
                                        background: '#fafafa',
                                        border: '1px solid #f0f0f0',
                                        borderRadius: 6,
                                        maxHeight: 320,
                                        overflowY: 'auto',
                                        fontSize: 13,
                                    }}
                                />
                            )}
                            {expanded && meta.inboundEmailId && meta.attachments?.length > 0 && (
                                <Space size={4} wrap style={{ marginTop: 6 }}>
                                    {(meta.attachments as {
                                        index: number;
                                        filename: string;
                                        size: number;
                                        stored: boolean;
                                    }[]).map((a) => (
                                        <Button
                                            key={a.index}
                                            size="small"
                                            icon={<PaperClipOutlined />}
                                            disabled={!a.stored}
                                            onClick={async () => {
                                                try {
                                                    const { url } = await getInboundAttachmentUrl({
                                                        jwtToken,
                                                        id: meta.inboundEmailId,
                                                        index: a.index,
                                                    }).unwrap();
                                                    window.open(url, '_blank', 'noopener');
                                                } catch (e: any) {
                                                    message.error(e?.data?.message || 'Attachment unavailable');
                                                }
                                            }}
                                        >
                                            {a.filename} ({Math.round(a.size / 1024)} KB)
                                        </Button>
                                    ))}
                                </Space>
                            )}
                            {expanded && !meta.inboundEmailId && meta.attachments?.length > 0 && (
                                <Space size={4} wrap style={{ marginTop: 6 }}>
                                    {(meta.attachments as {
                                        filename: string;
                                        size: number;
                                        s3Key: string;
                                    }[]).map((a) => (
                                        <Button
                                            key={a.s3Key || a.filename}
                                            size="small"
                                            icon={<PaperClipOutlined />}
                                            disabled={!a.s3Key}
                                            onClick={async () => {
                                                try {
                                                    const { url } = await getSentAttachmentUrl({
                                                        jwtToken,
                                                        key: a.s3Key,
                                                    }).unwrap();
                                                    window.open(url, '_blank', 'noopener');
                                                } catch (e: any) {
                                                    message.error(e?.data?.message || 'Attachment unavailable');
                                                }
                                            }}
                                        >
                                            {a.filename} ({Math.round(a.size / 1024)} KB)
                                        </Button>
                                    ))}
                                </Space>
                            )}
                        </div>
                    )}
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(activity.occurredAt).format('YYYY-MM-DD HH:mm')}
                            {activity.createdByName ? ` · ${activity.createdByName}` : ''}
                            {` · `}
                            <FormattedMessage
                                id={`crm.activityType.${activity.type}`}
                                defaultMessage={activity.type}
                            />
                        </Text>
                    </div>
                </>
            ),
        };
    };

    // CRM activities and product activity interleaved, newest first — both
    // carry UTC timestamps so they order against each other correctly.
    const mergedTimelineItems = useMemo(() => {
        const crmItems = (activities?.data || []).map((a: ICrmActivity) => ({
            at: a.occurredAt,
            item: activityToTimelineItem(a),
        }));
        const productItems = productTimelineItems.map((p, i) => ({
            at: p.at,
            item: { key: `product-${i}`, color: p.color, children: p.node },
        }));
        return [...crmItems, ...productItems]
            .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
            .map((entry) => entry.item);
        // activityToTimelineItem is recreated each render and intentionally
        // omitted; expandedEmails must be here, or toggling "Read message"
        // changes state without rebuilding the items that read it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activities, productTimelineItems, expandedEmails]);

    if (!companyId) return null;

    const handleStageChange = async (stageId: number) => {
        const stage = stages.find((s) => s.id === stageId);
        if (!stage || !company) return;
        if (stage.isLost) {
            let lostReason = company.lostReason ?? undefined;
            Modal.confirm({
                title: intl.formatMessage({ id: 'crm.moveToLost.title' }),
                content: (
                    <Select
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder={intl.formatMessage({ id: 'crm.lostReasonRequired' })}
                        options={LOST_REASONS.map((r) => ({
                            value: r,
                            label: intl.formatMessage({ id: `crm.lostReason.${r}` }),
                        }))}
                        onChange={(v) => {
                            lostReason = v;
                        }}
                    />
                ),
                onOk: async () => {
                    if (!lostReason) {
                        message.warning(intl.formatMessage({ id: 'crm.lostReasonRequired' }));
                        return Promise.reject();
                    }
                    await updateCompany({
                        jwtToken,
                        id: company.id,
                        payload: { pipelineStageId: stageId, lostReason },
                    }).unwrap();
                },
            });
        } else {
            try {
                await updateCompany({
                    jwtToken,
                    id: company.id,
                    payload: { pipelineStageId: stageId },
                }).unwrap();
            } catch (e: any) {
                message.error(e?.data?.message || 'Failed to change stage');
            }
        }
    };

    const handleLink = async () => {
        if (!selectedOrgId) return;
        try {
            await linkCompany({ jwtToken, id: companyId, organisationId: selectedOrgId }).unwrap();
            message.success(intl.formatMessage({ id: 'crm.drawer.linked' }));
            setLinkModalOpen(false);
            setSelectedOrgId(null);
        } catch (e: any) {
            message.error(e?.data?.message || 'Failed to link organisation');
        }
    };

    const handleAddNote = async () => {
        if (!noteBody.trim()) return;
        await createActivity({
            jwtToken,
            companyId,
            payload: { type: 'note', body: noteBody.trim() },
        }).unwrap();
        setNoteBody('');
    };

    const handleSaveNotes = async () => {
        if (notesDraft === null) return;
        await updateCompany({
            jwtToken,
            id: companyId,
            payload: { sheetNotes: notesDraft },
        }).unwrap();
        setNotesDraft(null);
        message.success(intl.formatMessage({ id: 'crm.drawer.notesSaved' }));
    };


    return (
        <Drawer
            open={!!companyId}
            onClose={onClose}
            width={720}
            // App.css centre-aligns every .ant-drawer-body globally; emails and
            // timeline prose must read left-aligned.
            styles={{ body: { textAlign: 'left' } }}
            title={
                company ? (
                    <Space size={10} wrap>
                        <Text strong style={{ fontSize: 16 }}>
                            {company.name}
                        </Text>
                        <SourceIcon source={company.source} />
                        <LifecycleTag stage={company.lifecycleStage} />
                        <HealthDot score={company.healthScore} />
                    </Space>
                ) : (
                    ''
                )
            }
        >
            {isLoading || !company ? (
                <div style={{ textAlign: 'center', padding: 60 }}>
                    <Spin />
                </div>
            ) : (
                <>
                    <Space wrap style={{ marginBottom: 16 }}>
                        <Button
                            icon={<SendOutlined style={{ marginRight: 4 }} />}
                            onClick={() => {
                                const primaryEmail = (
                                    company.contacts.find((c) => c.isPrimary && c.email) ??
                                    company.contacts.find((c) => !!c.email)
                                )?.email;
                                emailForm.setFieldsValue({
                                    toPeople: primaryEmail
                                        ? [primaryEmail.toLowerCase()]
                                        : [],
                                    sendAsUserId: user?.id,
                                });
                                setEmailModalOpen(true);
                            }}
                        >
                            <FormattedMessage id="crm.email.send" defaultMessage="Send email" />
                        </Button>
                        <Select
                            value={company.pipelineStage.id}
                            style={{ minWidth: 160 }}
                            onChange={handleStageChange}
                            options={stages
                                .filter((s) => s.isActive)
                                .sort((a, b) => a.displayOrder - b.displayOrder)
                                .map((s) => ({
                                    value: s.id,
                                    label: (
                                        <Space size={6}>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: s.color,
                                                }}
                                            />
                                            {s.label}
                                        </Space>
                                    ),
                                }))}
                        />
                        {company.organisationId ? (
                            <>
                                <Link to={`/admin/organizations/${company.organisationId}`}>
                                    <Button icon={<LinkOutlined style={{ marginRight: 4 }} />}>
                                        <FormattedMessage id="crm.drawer.viewOrg" defaultMessage="View organisation" />
                                    </Button>
                                </Link>
                                <Popconfirm
                                    title={<FormattedMessage id="crm.drawer.unlinkConfirm" defaultMessage="Unlink this account from its organisation?" />}
                                    onConfirm={() => unlinkCompany({ jwtToken, id: companyId })}
                                >
                                    <Button danger icon={<DisconnectOutlined style={{ marginRight: 4 }} />}>
                                        <FormattedMessage id="crm.drawer.unlinkOrg" defaultMessage="Unlink" />
                                    </Button>
                                </Popconfirm>
                            </>
                        ) : (
                            <Button
                                type="primary"
                                icon={<LinkOutlined style={{ marginRight: 4 }} />}
                                onClick={() => setLinkModalOpen(true)}
                            >
                                <FormattedMessage id="crm.drawer.linkOrg" defaultMessage="Link to organisation" />
                            </Button>
                        )}
                    </Space>

                    {company.removedFromSheet && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={<FormattedMessage id="crm.removedFromSheet" defaultMessage="Row removed from sheet — review" />}
                        />
                    )}

                    <Tabs
                        items={[
                            {
                                key: 'overview',
                                label: <FormattedMessage id="crm.drawer.overview" defaultMessage="Overview" />,
                                children: (
                                    <>
                                        <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
                                            <Descriptions.Item label={<FormattedMessage id="crm.column.productType" defaultMessage="Product type" />}>
                                                {company.productType || '—'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={<FormattedMessage id="crm.column.owner" defaultMessage="Owner" />}>
                                                {company.ownerName || '—'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={<FormattedMessage id="crm.column.emailDomain" defaultMessage="Email domain" />}>
                                                {company.emailDomain || '—'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={<FormattedMessage id="crm.column.source" defaultMessage="Source" />}>
                                                <FormattedMessage id={`crm.source.${company.source}`} defaultMessage={company.source} />
                                            </Descriptions.Item>
                                            {company.holdReason && (
                                                <Descriptions.Item label={<FormattedMessage id="crm.column.holdReason" defaultMessage="Hold reason" />}>
                                                    <FormattedMessage id={`crm.holdReason.${company.holdReason}`} defaultMessage={company.holdReason} />
                                                </Descriptions.Item>
                                            )}
                                            {company.lostReason && (
                                                <Descriptions.Item label={<FormattedMessage id="crm.column.lostReason" defaultMessage="Lost reason" />}>
                                                    <FormattedMessage id={`crm.lostReason.${company.lostReason}`} defaultMessage={company.lostReason} />
                                                </Descriptions.Item>
                                            )}
                                        </Descriptions>

                                        {company.metadata?.sheetExtras &&
                                            Object.keys(company.metadata.sheetExtras).length > 0 && (
                                                <Descriptions
                                                    column={2}
                                                    size="small"
                                                    style={{ marginBottom: 16 }}
                                                    title={<FormattedMessage id="crm.drawer.sheetExtras" defaultMessage="Other sheet fields" />}
                                                >
                                                    {Object.entries(company.metadata.sheetExtras).map(([k, v]) => (
                                                        <Descriptions.Item key={k} label={k}>
                                                            {String(v) || '—'}
                                                        </Descriptions.Item>
                                                    ))}
                                                </Descriptions>
                                            )}

                                        <Card
                                            size="small"
                                            style={{ marginBottom: 16 }}
                                            title={<FormattedMessage id="crm.drawer.notes" defaultMessage="Notes (synced with sheet)" />}
                                            extra={
                                                notesDraft !== null && (
                                                    <Button size="small" type="primary" onClick={handleSaveNotes}>
                                                        <FormattedMessage id="crm.drawer.saveNotes" defaultMessage="Save" />
                                                    </Button>
                                                )
                                            }
                                        >
                                            <TextArea
                                                autoSize={{ minRows: 2, maxRows: 6 }}
                                                value={notesDraft ?? company.sheetNotes ?? ''}
                                                onChange={(e) => setNotesDraft(e.target.value)}
                                            />
                                        </Card>

                                        <Card
                                            size="small"
                                            style={{ marginBottom: 16 }}
                                            title={<FormattedMessage id="crm.drawer.contacts" defaultMessage="Contacts" />}
                                            extra={
                                                <Button
                                                    size="small"
                                                    icon={<PlusOutlined style={{ marginRight: 4 }} />}
                                                    onClick={() => setContactModalOpen(true)}
                                                >
                                                    <FormattedMessage id="crm.drawer.addContact" defaultMessage="Add" />
                                                </Button>
                                            }
                                        >
                                            <List
                                                size="small"
                                                dataSource={company.contacts}
                                                locale={{ emptyText: '—' }}
                                                renderItem={(contact) => (
                                                    <List.Item
                                                        actions={[
                                                            <Popconfirm
                                                                key="delete"
                                                                title={<FormattedMessage id="crm.drawer.deleteContact" defaultMessage="Remove this contact?" />}
                                                                onConfirm={() => deleteContact({ jwtToken, contactId: contact.id })}
                                                            >
                                                                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                                            </Popconfirm>,
                                                        ]}
                                                    >
                                                        <Space size={8} wrap>
                                                            <UserOutlined style={{ color: '#8c8c8c' }} />
                                                            <Text strong>{contact.name || '—'}</Text>
                                                            {contact.position && <Text type="secondary">{contact.position}</Text>}
                                                            {contact.email && (
                                                                <a href={`mailto:${contact.email}`}>
                                                                    <MailOutlined style={{ marginRight: 4 }} />
                                                                    {contact.email}
                                                                </a>
                                                            )}
                                                            {contact.isPrimary && (
                                                                <Tag color="blue">
                                                                    <FormattedMessage id="crm.drawer.primary" defaultMessage="Primary" />
                                                                </Tag>
                                                            )}
                                                        </Space>
                                                    </List.Item>
                                                )}
                                            />
                                        </Card>

                                        {company.organisationId ? (
                                            <>
                                                {company.usage && (
                                                    <Card
                                                        size="small"
                                                        style={{ marginBottom: 16 }}
                                                        title={<FormattedMessage id="crm.drawer.usage" defaultMessage="Platform usage" />}
                                                    >
                                                        <Row gutter={[16, 16]}>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.usage.responses30" defaultMessage="Responses (30d)" />}
                                                                    value={company.usage.responsesLast30Days}
                                                                    suffix={
                                                                        company.usage.responsesPrior30Days > 0 ? (
                                                                            <Text
                                                                                type={
                                                                                    company.usage.responsesLast30Days >=
                                                                                    company.usage.responsesPrior30Days
                                                                                        ? 'success'
                                                                                        : 'danger'
                                                                                }
                                                                                style={{ fontSize: 12 }}
                                                                            >
                                                                                {company.usage.responsesLast30Days >=
                                                                                company.usage.responsesPrior30Days
                                                                                    ? '↑'
                                                                                    : '↓'}{' '}
                                                                                vs {company.usage.responsesPrior30Days}
                                                                            </Text>
                                                                        ) : undefined
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.usage.responses" defaultMessage="Total responses" />}
                                                                    value={company.usage.responseCount}
                                                                />
                                                            </Col>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.usage.lastLogin" defaultMessage="Last login" />}
                                                                    value={
                                                                        company.usage.lastLoginAt
                                                                            ? dayjs(company.usage.lastLoginAt).format('YYYY-MM-DD')
                                                                            : '—'
                                                                    }
                                                                    valueStyle={{ fontSize: 16 }}
                                                                />
                                                            </Col>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.usage.users" defaultMessage="Users" />}
                                                                    value={company.usage.userCount}
                                                                />
                                                            </Col>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.usage.brands" defaultMessage="Brands" />}
                                                                    value={company.usage.brandCount}
                                                                />
                                                            </Col>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.usage.surveys" defaultMessage="Surveys" />}
                                                                    value={company.usage.surveyCount}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card>
                                                )}
                                                {company.metadata?.posthog && (
                                                    <Card
                                                        size="small"
                                                        style={{ marginBottom: 16 }}
                                                        title={<FormattedMessage id="crm.drawer.posthog" defaultMessage="Product analytics (30d)" />}
                                                        extra={
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                <FormattedMessage
                                                                    id="crm.posthog.computedAt"
                                                                    defaultMessage="As of {date}"
                                                                    values={{
                                                                        date: dayjs(company.metadata.posthog.computedAt).format('MM-DD HH:mm'),
                                                                    }}
                                                                />
                                                            </Text>
                                                        }
                                                    >
                                                        <Row gutter={[16, 16]}>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.posthog.events" defaultMessage="Events" />}
                                                                    value={company.metadata.posthog.events30d}
                                                                />
                                                            </Col>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.posthog.activeDays" defaultMessage="Active days" />}
                                                                    value={company.metadata.posthog.activeDays30d}
                                                                    suffix="/ 30"
                                                                />
                                                            </Col>
                                                            <Col span={8}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.posthog.lastSeen" defaultMessage="Last seen" />}
                                                                    value={
                                                                        company.metadata.posthog.lastSeenAt
                                                                            ? dayjs(company.metadata.posthog.lastSeenAt).format('YYYY-MM-DD')
                                                                            : '—'
                                                                    }
                                                                    valueStyle={{ fontSize: 16 }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        {(company.metadata.posthog.topPages || []).length > 0 && (
                                                            <div style={{ marginTop: 12 }}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    <FormattedMessage id="crm.posthog.topPages" defaultMessage="Top pages" />
                                                                </Text>
                                                                {company.metadata.posthog.topPages.map(
                                                                    (p: { path: string; count: number }) => (
                                                                        <div
                                                                            key={p.path}
                                                                            style={{ display: 'flex', justifyContent: 'space-between' }}
                                                                        >
                                                                            <Text ellipsis style={{ maxWidth: '80%' }}>{p.path}</Text>
                                                                            <Text type="secondary">{p.count}</Text>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        )}
                                                    </Card>
                                                )}
                                                {company.emailEngagement && (
                                                    <Card
                                                        size="small"
                                                        style={{ marginBottom: 16 }}
                                                        title={<FormattedMessage id="crm.drawer.emailEngagement" defaultMessage="Email engagement (30d)" />}
                                                    >
                                                        <Row gutter={[16, 16]}>
                                                            <Col span={6}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.email.sends" defaultMessage="Sent" />}
                                                                    value={company.emailEngagement.sendsLast30Days}
                                                                />
                                                            </Col>
                                                            <Col span={6}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.email.opens" defaultMessage="Opens" />}
                                                                    value={company.emailEngagement.opensLast30Days}
                                                                />
                                                            </Col>
                                                            <Col span={6}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.email.clicks" defaultMessage="Clicks" />}
                                                                    value={company.emailEngagement.clicksLast30Days}
                                                                />
                                                            </Col>
                                                            <Col span={6}>
                                                                <Statistic
                                                                    title={<FormattedMessage id="crm.email.lastOpen" defaultMessage="Last open" />}
                                                                    value={
                                                                        company.emailEngagement.lastOpenAt
                                                                            ? dayjs(company.emailEngagement.lastOpenAt).format('YYYY-MM-DD')
                                                                            : '—'
                                                                    }
                                                                    valueStyle={{ fontSize: 16 }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card>
                                                )}
                                                {company.subscription && (
                                                    <Card
                                                        size="small"
                                                        title={<FormattedMessage id="crm.drawer.subscription" defaultMessage="Subscription" />}
                                                    >
                                                        <Descriptions column={2} size="small">
                                                            <Descriptions.Item label={<FormattedMessage id="crm.subscription.plan" defaultMessage="Plan" />}>
                                                                {company.subscription.planName || company.subscription.planCode || '—'}
                                                            </Descriptions.Item>
                                                            <Descriptions.Item label={<FormattedMessage id="crm.subscription.status" defaultMessage="Status" />}>
                                                                <Tag
                                                                    color={
                                                                        company.subscription.status === 'active'
                                                                            ? 'green'
                                                                            : company.subscription.status === 'past_due'
                                                                              ? 'orange'
                                                                              : company.subscription.status === 'canceled'
                                                                                ? 'red'
                                                                                : 'blue'
                                                                    }
                                                                >
                                                                    {company.subscription.status}
                                                                </Tag>
                                                            </Descriptions.Item>
                                                            {company.subscription.trialEnd && (
                                                                <Descriptions.Item label={<FormattedMessage id="crm.subscription.trialEnds" defaultMessage="Trial ends" />}>
                                                                    {dayjs(company.subscription.trialEnd).format('YYYY-MM-DD')}
                                                                </Descriptions.Item>
                                                            )}
                                                            {company.subscription.currentPeriodEnd && (
                                                                <Descriptions.Item label={<FormattedMessage id="crm.subscription.renews" defaultMessage="Current period ends" />}>
                                                                    {dayjs(company.subscription.currentPeriodEnd).format('YYYY-MM-DD')}
                                                                </Descriptions.Item>
                                                            )}
                                                        </Descriptions>
                                                    </Card>
                                                )}
                                            </>
                                        ) : (
                                            <Alert
                                                type="info"
                                                showIcon
                                                message={<FormattedMessage id="crm.drawer.noOrg" defaultMessage="Not linked to a platform organisation yet" />}
                                            />
                                        )}
                                    </>
                                ),
                            },
                            {
                                key: 'timeline',
                                label: <FormattedMessage id="crm.drawer.timeline" defaultMessage="Timeline" />,
                                children: (
                                    <>
                                        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                                            <Input
                                                placeholder={intl.formatMessage({ id: 'crm.drawer.addNote' })}
                                                value={noteBody}
                                                onChange={(e) => setNoteBody(e.target.value)}
                                                onPressEnter={handleAddNote}
                                            />
                                            <Button
                                                type="primary"
                                                loading={createActivityResult.isLoading}
                                                onClick={handleAddNote}
                                            >
                                                <FormattedMessage id="crm.drawer.logNote" defaultMessage="Log note" />
                                            </Button>
                                        </Space.Compact>
                                        {/* No inbound mail yet — replies are pasted in by hand,
                                            stamped with when they actually arrived. */}
                                        <Button
                                            icon={<MailOutlined style={{ marginRight: 4 }} />}
                                            style={{ marginBottom: 12 }}
                                            onClick={() => {
                                                receivedForm.setFieldsValue({
                                                    receivedAt: dayjs(),
                                                    fromEmail:
                                                        company.contacts.find((c) => c.isPrimary && c.email)?.email ??
                                                        company.contacts.find((c) => !!c.email)?.email,
                                                });
                                                setReceivedModalOpen(true);
                                            }}
                                        >
                                            <FormattedMessage id="crm.email.logReceived" defaultMessage="Log received email" />
                                        </Button>
                                        {company.organisationId && (
                                            <div style={{ marginBottom: 12 }}>
                                                <Switch
                                                    size="small"
                                                    checked={showProductActivity}
                                                    onChange={setShowProductActivity}
                                                    style={{ marginRight: 8 }}
                                                />
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    <FormattedMessage
                                                        id="crm.drawer.showProductActivity"
                                                        defaultMessage="Include product activity (logins, emails opened/clicked, in-app sessions)"
                                                    />
                                                </Text>
                                            </div>
                                        )}
                                        <Timeline items={mergedTimelineItems} />
                                    </>
                                ),
                            },
                            {
                                key: 'tasks',
                                label: <FormattedMessage id="crm.drawer.tasks" defaultMessage="Tasks" />,
                                children: (
                                    <>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined style={{ marginRight: 4 }} />}
                                            style={{ marginBottom: 12 }}
                                            onClick={() => setTaskModalOpen(true)}
                                        >
                                            <FormattedMessage id="crm.drawer.newTask" defaultMessage="New task" />
                                        </Button>
                                        <List
                                            size="small"
                                            dataSource={(tasks || []).filter((t) => t.status !== 'cancelled')}
                                            locale={{ emptyText: '—' }}
                                            renderItem={(task) => (
                                                <List.Item
                                                    actions={
                                                        task.status === 'open'
                                                            ? [
                                                                  <Button
                                                                      key="done"
                                                                      size="small"
                                                                      icon={<CheckOutlined style={{ marginRight: 4 }} />}
                                                                      onClick={() =>
                                                                          updateTask({
                                                                              jwtToken,
                                                                              id: task.id,
                                                                              payload: { status: 'done' },
                                                                          })
                                                                      }
                                                                  >
                                                                      <FormattedMessage id="crm.task.markDone" defaultMessage="Done" />
                                                                  </Button>,
                                                                  <Popconfirm
                                                                      key="delete"
                                                                      title={
                                                                          <FormattedMessage
                                                                              id="crm.task.deleteConfirm"
                                                                              defaultMessage="Delete this task?"
                                                                          />
                                                                      }
                                                                      onConfirm={() =>
                                                                          updateTask({
                                                                              jwtToken,
                                                                              id: task.id,
                                                                              payload: { status: 'cancelled' },
                                                                          })
                                                                      }
                                                                  >
                                                                      <Button
                                                                          size="small"
                                                                          danger
                                                                          icon={<DeleteOutlined />}
                                                                      />
                                                                  </Popconfirm>,
                                                              ]
                                                            : []
                                                    }
                                                >
                                                    <List.Item.Meta
                                                        title={
                                                            <Space size={6}>
                                                                <Text delete={task.status !== 'open'}>{task.title}</Text>
                                                                {task.reminderEnabled && (
                                                                    <Tooltip
                                                                        title={
                                                                            <FormattedMessage
                                                                                id="crm.task.reminderTooltip"
                                                                                defaultMessage="Reminder: emails the creator when due"
                                                                            />
                                                                        }
                                                                    >
                                                                        <BellOutlined
                                                                            style={{
                                                                                color:
                                                                                    task.status === 'open' &&
                                                                                    task.dueAt &&
                                                                                    isPast(task.dueAt)
                                                                                        ? '#ff4d4f'
                                                                                        : '#8c8c8c',
                                                                                fontSize: 12,
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space size={8}>
                                                                {task.dueAt && (
                                                                    <Text
                                                                        type={
                                                                            task.status === 'open' &&
                                                                            isPast(task.dueAt)
                                                                                ? 'danger'
                                                                                : 'secondary'
                                                                        }
                                                                        style={{ fontSize: 12 }}
                                                                    >
                                                                        <FormattedMessage
                                                                            id="crm.task.dueAtAest"
                                                                            defaultMessage="{when} AEST"
                                                                            values={{ when: formatAest(task.dueAt) }}
                                                                        />
                                                                    </Text>
                                                                )}
                                                                {task.assignedToName && (
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                                        {task.assignedToName}
                                                                    </Text>
                                                                )}
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </>
                                ),
                            },
                        ]}
                    />

                    <Modal
                        open={linkModalOpen}
                        title={<FormattedMessage id="crm.drawer.linkOrg" defaultMessage="Link to organisation" />}
                        onCancel={() => setLinkModalOpen(false)}
                        onOk={handleLink}
                        okButtonProps={{ disabled: !selectedOrgId }}
                    >
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            placeholder={intl.formatMessage({ id: 'crm.drawer.searchOrg' })}
                            filterOption={false}
                            loading={orgSearchLoading}
                            onSearch={setOrgSearch}
                            onChange={(v) => setSelectedOrgId(v)}
                            options={(orgSearchResults?.data || []).map((org) => ({
                                value: org.id,
                                label: org.name,
                            }))}
                        />
                    </Modal>

                    <Modal
                        open={emailModalOpen}
                        width={640}
                        title={
                            emailPreview ? (
                                <FormattedMessage id="crm.email.previewTitle" defaultMessage="Review before sending" />
                            ) : (
                                <FormattedMessage id="crm.email.composeTitle" defaultMessage="Send email" />
                            )
                        }
                        onCancel={() => {
                            // From the preview step, "cancel" means go back to the
                            // compose form (keep the draft). From the compose form
                            // it closes the modal — reset so a re-open starts clean
                            // rather than showing the previous draft.
                            if (emailPreview) {
                                setEmailPreview(null);
                            } else {
                                emailForm.resetFields();
                                setEmailAttachments([]);
                                setSelectedTemplateId(undefined);
                                setEmailModalOpen(false);
                            }
                        }}
                        cancelText={
                            emailPreview ? (
                                <FormattedMessage id="crm.email.back" defaultMessage="Back to edit" />
                            ) : undefined
                        }
                        okButtonProps={{ loading: sendCrmEmailResult.isLoading }}
                        okText={
                            emailPreview ? (
                                <FormattedMessage id="crm.email.confirmSend" defaultMessage="Confirm & send" />
                            ) : (
                                <FormattedMessage id="crm.email.review" defaultMessage="Review" />
                            )
                        }
                        onOk={async () => {
                            if (!emailPreview) {
                                emailForm.submit();
                                return;
                            }
                            try {
                                const result = await sendCrmEmail({
                                    jwtToken,
                                    companyId,
                                    payload: {
                                        crmContactId: emailPreview.crmContactId,
                                        to: emailPreview.to,
                                        cc: emailPreview.cc.length ? emailPreview.cc : undefined,
                                        subject: emailPreview.subject,
                                        body: emailPreview.body,
                                        sendAsUserId: emailPreview.sendAsUserId,
                                        branded: emailPreview.branded,
                                        allowUnfilledPlaceholders:
                                            emailPreview.allowUnfilledPlaceholders,
                                        attachmentIds: emailPreview.attachments.length
                                            ? emailPreview.attachments.map((a) => a.id)
                                            : undefined,
                                    },
                                }).unwrap();
                                message.success(
                                    intl.formatMessage({ id: 'crm.email.sent' }, { to: result.to }),
                                );
                                emailForm.resetFields();
                                setEmailAttachments([]);
                                setSelectedTemplateId(undefined);
                                setEmailPreview(null);
                                setEmailModalOpen(false);
                            } catch (e: any) {
                                message.error(e?.data?.message || 'Failed to send email');
                            }
                        }}
                    >
                        {emailPreview && (
                            <div>
                                <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
                                    <Descriptions.Item label={<FormattedMessage id="crm.email.previewFrom" defaultMessage="From" />}>
                                        "{emailPreview.fromLabel}"
                                        {emailPreview.fromAddress && (
                                            <Text type="secondary" style={{ marginLeft: 6 }}>
                                                &lt;{emailPreview.fromAddress}&gt;
                                            </Text>
                                        )}
                                        <Tag color={emailPreview.branded ? 'gold' : 'default'} style={{ marginLeft: 8 }}>
                                            {emailPreview.branded
                                                ? intl.formatMessage({ id: 'crm.email.styleBranded' })
                                                : intl.formatMessage({ id: 'crm.email.stylePlain' })}
                                        </Tag>
                                        {emailPreview.sendAsUserId !== user?.id && (
                                            <Tag color="orange" style={{ marginLeft: 8 }}>
                                                <FormattedMessage
                                                    id="crm.email.sendingAsOther"
                                                    defaultMessage="Sending on behalf of {name}"
                                                    values={{ name: selectedSender?.name || '' }}
                                                />
                                            </Tag>
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<FormattedMessage id="crm.email.to" defaultMessage="To" />}>
                                        <Text strong>{emailPreview.to.join(', ')}</Text>
                                    </Descriptions.Item>
                                    {emailPreview.cc.length > 0 && (
                                        <Descriptions.Item label={<FormattedMessage id="crm.email.cc" defaultMessage="CC" />}>
                                            <Text strong>{emailPreview.cc.join(', ')}</Text>
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item label={<FormattedMessage id="crm.email.subject" defaultMessage="Subject" />}>
                                        <Text strong>{emailPreview.subject}</Text>
                                    </Descriptions.Item>
                                    {emailPreview.attachments.length > 0 && (
                                        <Descriptions.Item label={<FormattedMessage id="crm.email.attachments" defaultMessage="Attachments" />}>
                                            <Space size={4} wrap>
                                                {emailPreview.attachments.map((a) => (
                                                    <Tag key={a.id} icon={<PaperClipOutlined />}>
                                                        {a.filename} ({Math.round(a.size / 1024)} KB)
                                                    </Tag>
                                                ))}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                                <EmailBodyHtml
                                    body={emailPreview.body}
                                    style={{
                                        border: '1px solid #f0f0f0',
                                        borderRadius: 8,
                                        padding: '12px 16px',
                                        background: '#fafafa',
                                        maxHeight: 360,
                                        overflowY: 'auto',
                                    }}
                                />
                            </div>
                        )}
                        <Form
                            form={emailForm}
                            layout="vertical"
                            style={emailPreview ? { display: 'none' } : undefined}
                            onFinish={(values) => {
                                // Resolve recipients exactly as the server will:
                                // chosen people + additional To tags, CC separate,
                                // all deduped lowercase.
                                const dedupe = (raw: (string | null | undefined)[]): string[] =>
                                    Array.from(
                                        new Set(
                                            raw
                                                .map((a) => a?.trim().toLowerCase())
                                                .filter((a): a is string => !!a),
                                        ),
                                    );
                                const toRecipients = dedupe([
                                    ...((values.toPeople as string[] | undefined) || []),
                                    ...((values.to as string[] | undefined) || []),
                                ]);
                                // Link the activity to a CRM contact when a
                                // recipient matches one.
                                const contact = company.contacts.find(
                                    (c) =>
                                        c.email &&
                                        toRecipients.includes(c.email.toLowerCase()),
                                );
                                const ccRecipients = dedupe(
                                    (values.cc as string[] | undefined) || [],
                                ).filter((a) => !toRecipients.includes(a));
                                if (toRecipients.length === 0) {
                                    message.error(
                                        intl.formatMessage({ id: 'crm.email.noRecipient' }),
                                    );
                                    return;
                                }
                                const invalid = [...toRecipients, ...ccRecipients].filter(
                                    (a) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a),
                                );
                                if (invalid.length) {
                                    message.error(
                                        intl.formatMessage(
                                            { id: 'crm.email.invalidRecipients' },
                                            { addresses: invalid.join(', ') },
                                        ),
                                    );
                                    return;
                                }
                                // Resolve known placeholders whether they came from a
                                // template or were typed by hand — the preview must show
                                // exactly what will be sent.
                                const subject = substituteTemplate(
                                    values.subject,
                                    toRecipients[0],
                                );
                                const body = substituteTemplate(
                                    values.body,
                                    toRecipients[0],
                                );
                                const proceed = (allowUnfilledPlaceholders: boolean) =>
                                    setEmailPreview({
                                        crmContactId: contact?.id,
                                        to: toRecipients,
                                        cc: ccRecipients,
                                        subject,
                                        body,
                                        sendAsUserId: values.sendAsUserId,
                                        fromLabel: senderFromLabel,
                                        fromAddress: senderAddress,
                                        branded: !!values.branded,
                                        allowUnfilledPlaceholders,
                                        attachments: emailAttachments,
                                    });
                                // {placeholders} still left after substitution are usually
                                // typo'd template variables — but sometimes deliberate
                                // (e.g. a setup pack telling the customer to paste a
                                // survey link into their own email platform), so
                                // warn-and-confirm rather than hard-stop. Double-braced
                                // merge tags ({{email}}) are always deliberate and pass
                                // silently.
                                const unfilled = `${subject} ${body}`.match(
                                    /(?<!\{)\{[a-zA-Z][a-zA-Z0-9_]*\}(?!\})/g,
                                );
                                if (unfilled) {
                                    Modal.confirm({
                                        title: intl.formatMessage({
                                            id: 'crm.email.placeholdersWarningTitle',
                                        }),
                                        content: intl.formatMessage(
                                            { id: 'crm.email.placeholdersWarningBody' },
                                            { placeholders: Array.from(new Set(unfilled)).join(', ') },
                                        ),
                                        okText: intl.formatMessage({
                                            id: 'crm.email.placeholdersWarningContinue',
                                        }),
                                        okButtonProps: { danger: true },
                                        onOk: () => proceed(true),
                                    });
                                    return;
                                }
                                proceed(false);
                            }}
                        >
                            <Form.Item
                                name="sendAsUserId"
                                label={<FormattedMessage id="crm.email.from" defaultMessage="From" />}
                            >
                                <Select
                                    placeholder={intl.formatMessage({ id: 'crm.email.fromPlaceholder' })}
                                    options={(emailSenders || []).map((s) => ({
                                        value: s.userId,
                                        label:
                                            s.userId === user?.id
                                                ? `${s.fromLabel} <${s.address}> (${intl.formatMessage({ id: 'crm.email.fromYou' })})`
                                                : `${s.fromLabel} <${s.address}>`,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item
                                name="toPeople"
                                label={<FormattedMessage id="crm.email.to" defaultMessage="To" />}
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    optionFilterProp="label"
                                    placeholder={intl.formatMessage({ id: 'crm.email.toPlaceholder' })}
                                    options={peopleOptions}
                                />
                            </Form.Item>
                            <Form.Item
                                name="to"
                                label={<FormattedMessage id="crm.email.toAdditional" defaultMessage="Additional recipients" />}
                            >
                                <Select
                                    mode="tags"
                                    tokenSeparators={[',', ' ', ';']}
                                    open={false}
                                    suffixIcon={null}
                                    placeholder={intl.formatMessage({
                                        id: 'crm.email.recipientsPlaceholder',
                                    })}
                                />
                            </Form.Item>
                            <Form.Item
                                name="cc"
                                label={<FormattedMessage id="crm.email.cc" defaultMessage="CC" />}
                            >
                                <Select
                                    mode="tags"
                                    tokenSeparators={[',', ' ', ';']}
                                    optionFilterProp="label"
                                    options={peopleOptions}
                                    placeholder={intl.formatMessage({
                                        id: 'crm.email.ccPlaceholder',
                                    })}
                                />
                            </Form.Item>
                            <Form.Item label={<FormattedMessage id="crm.email.template" defaultMessage="Template" />}>
                                <Select
                                    allowClear
                                    value={selectedTemplateId}
                                    placeholder={intl.formatMessage({ id: 'crm.email.templatePlaceholder' })}
                                    options={(emailTemplates || []).map((t) => ({
                                        value: t.id,
                                        label: t.name,
                                    }))}
                                    onChange={(templateId) => {
                                        setSelectedTemplateId(templateId);
                                        const template = (emailTemplates || []).find(
                                            (t) => t.id === templateId,
                                        );
                                        if (!template) return;
                                        const firstRecipient = (
                                            (emailForm.getFieldValue('toPeople') as string[] | undefined) || []
                                        )[0];
                                        emailForm.setFieldsValue({
                                            subject: substituteTemplate(template.subjectTemplate, firstRecipient),
                                            body: substituteTemplate(template.bodyHtmlTemplate, firstRecipient),
                                        });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="subject"
                                rules={[{ required: true }]}
                                label={<FormattedMessage id="crm.email.subject" defaultMessage="Subject" />}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="body"
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            isQuillEmpty(value)
                                                ? Promise.reject(
                                                      intl.formatMessage({ id: 'crm.email.bodyRequired' }),
                                                  )
                                                : Promise.resolve(),
                                    },
                                ]}
                                label={<FormattedMessage id="crm.email.body" defaultMessage="Message" />}
                                extra={
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 4,
                                            paddingTop: 8,
                                        }}
                                    >
                                        <Text type="secondary" style={{ fontSize: 12, marginRight: 2 }}>
                                            <FormattedMessage
                                                id="crm.email.placeholdersHint"
                                                defaultMessage="Placeholders (filled with real values at Review):"
                                            />
                                        </Text>
                                        {[
                                            '{contactName}',
                                            '{firstName}',
                                            '{companyName}',
                                            '{senderName}',
                                        ].map((token) => (
                                            <Tag
                                                key={token}
                                                style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: 11,
                                                    marginRight: 0,
                                                }}
                                            >
                                                {token}
                                            </Tag>
                                        ))}
                                    </div>
                                }
                            >
                                <CrmRichTextEditor
                                    placeholder={intl.formatMessage({ id: 'crm.email.bodyPlaceholder' })}
                                />
                            </Form.Item>
                            <Space wrap style={{ marginBottom: 12 }}>
                                <Upload beforeUpload={handleAttachmentUpload} showUploadList={false} multiple>
                                    <Button size="small" icon={<PaperClipOutlined />} loading={uploadingAttachment}>
                                        <FormattedMessage id="mail.attach" defaultMessage="Attach file" />
                                    </Button>
                                </Upload>
                                {emailAttachments.map((a) => (
                                    <Tag
                                        key={a.id}
                                        icon={<PaperClipOutlined />}
                                        closable
                                        closeIcon={<CloseOutlined />}
                                        onClose={() =>
                                            setEmailAttachments((current) => current.filter((x) => x.id !== a.id))
                                        }
                                    >
                                        {a.filename} ({Math.round(a.size / 1024)} KB)
                                    </Tag>
                                ))}
                            </Space>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                                <Form.Item name="branded" valuePropName="checked" noStyle>
                                    <Switch size="small" />
                                </Form.Item>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <FormattedMessage
                                        id="crm.email.brandedToggle"
                                        defaultMessage="Use the branded template. Off = a plain personal email, which reads less like marketing automation for one-to-one outreach."
                                    />
                                </Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage
                                    id="crm.email.senderPreview"
                                    defaultMessage='Will be sent as "{fromLabel}" {fromAddress} — opens and clicks appear on the timeline. Replies go to the shared inbox.'
                                    // Angle brackets are wrapped here, not in the message: ICU
                                    // would try to read a literal "<" as a rich-text tag.
                                    values={{ fromLabel: senderFromLabel, fromAddress: senderAddress ? `<${senderAddress}>` : '' }}
                                />
                            </Text>
                        </Form>
                    </Modal>

                    <Modal
                        open={receivedModalOpen}
                        title={<FormattedMessage id="crm.email.logReceivedTitle" defaultMessage="Log a received email" />}
                        onCancel={() => setReceivedModalOpen(false)}
                        okText={<FormattedMessage id="crm.email.logReceivedOk" defaultMessage="Add to timeline" />}
                        okButtonProps={{ loading: logReceivedEmailResult.isLoading }}
                        onOk={() => receivedForm.submit()}
                        width={640}
                    >
                        <Form form={receivedForm} layout="vertical" onFinish={handleLogReceived}>
                            <Form.Item
                                name="receivedAt"
                                label={<FormattedMessage id="crm.email.receivedAt" defaultMessage="Received at" />}
                                rules={[{ required: true }]}
                                extra={intl.formatMessage({ id: 'crm.email.receivedAtHelp' })}
                            >
                                <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
                            </Form.Item>
                            <Form.Item
                                name="fromEmail"
                                label={<FormattedMessage id="crm.email.receivedFrom" defaultMessage="From" />}
                            >
                                <Input type="email" placeholder="name@company.com" />
                            </Form.Item>
                            <Form.Item
                                name="subject"
                                rules={[{ required: true }]}
                                label={<FormattedMessage id="crm.email.subject" defaultMessage="Subject" />}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="body"
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            isQuillEmpty(value)
                                                ? Promise.reject(
                                                      intl.formatMessage({ id: 'crm.email.bodyRequired' }),
                                                  )
                                                : Promise.resolve(),
                                    },
                                ]}
                                label={<FormattedMessage id="crm.email.receivedBody" defaultMessage="Paste the message" />}
                            >
                                <CrmRichTextEditor minHeight={240} />
                            </Form.Item>
                        </Form>
                    </Modal>

                    <Modal
                        open={contactModalOpen}
                        title={<FormattedMessage id="crm.drawer.addContact" defaultMessage="Add contact" />}
                        onCancel={() => setContactModalOpen(false)}
                        onOk={() => contactForm.submit()}
                    >
                        <Form
                            form={contactForm}
                            layout="vertical"
                            onFinish={async (values) => {
                                await createContact({ jwtToken, companyId, payload: values }).unwrap();
                                contactForm.resetFields();
                                setContactModalOpen(false);
                            }}
                        >
                            <Form.Item name="name" label={<FormattedMessage id="crm.contact.name" defaultMessage="Name" />}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="email" label={<FormattedMessage id="crm.contact.email" defaultMessage="Email" />}>
                                <Input type="email" />
                            </Form.Item>
                            <Form.Item name="position" label={<FormattedMessage id="crm.contact.position" defaultMessage="Position" />}>
                                <Input />
                            </Form.Item>
                        </Form>
                    </Modal>

                    <Modal
                        open={taskModalOpen}
                        title={<FormattedMessage id="crm.drawer.newTask" defaultMessage="New task" />}
                        onCancel={() => setTaskModalOpen(false)}
                        onOk={() => taskForm.submit()}
                    >
                        <Form
                            form={taskForm}
                            layout="vertical"
                            onFinish={async (values) => {
                                await createTask({
                                    jwtToken,
                                    payload: {
                                        crmCompanyId: companyId,
                                        title: values.title,
                                        details: values.details,
                                        // Picker wall-clock is fixed AEST (SYR-95).
                                        dueAt: values.dueAt ? utcFromAestWall(values.dueAt) : undefined,
                                        reminderEnabled: !!values.reminderEnabled,
                                    },
                                }).unwrap();
                                taskForm.resetFields();
                                setTaskModalOpen(false);
                            }}
                        >
                            <Form.Item
                                name="title"
                                rules={[{ required: true }]}
                                label={<FormattedMessage id="crm.task.title" defaultMessage="Title" />}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item name="details" label={<FormattedMessage id="crm.task.details" defaultMessage="Details" />}>
                                <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                            </Form.Item>
                            <Form.Item
                                name="dueAt"
                                label={<FormattedMessage id="crm.task.dueAest" defaultMessage="Due (AEST)" />}
                                dependencies={['reminderEnabled']}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator: (_, value) =>
                                            getFieldValue('reminderEnabled') && !value
                                                ? Promise.reject(
                                                      new Error(
                                                          intl.formatMessage({
                                                              id: 'crm.task.reminderNeedsDue',
                                                              defaultMessage:
                                                                  'A reminder needs a due date and time',
                                                          }),
                                                      ),
                                                  )
                                                : Promise.resolve(),
                                    }),
                                ]}
                            >
                                <DatePicker
                                    showTime={{ format: 'HH:mm', minuteStep: 5 }}
                                    format="YYYY-MM-DD HH:mm"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="reminderEnabled"
                                valuePropName="checked"
                                style={{ marginBottom: 0 }}
                            >
                                <Checkbox>
                                    <FormattedMessage
                                        id="crm.task.reminderEnabled"
                                        defaultMessage="Email me when this is due"
                                    />
                                </Checkbox>
                            </Form.Item>
                        </Form>
                    </Modal>
                </>
            )}
        </Drawer>
    );
};
